import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const shopLinks = [
  { label: '商品分類', href: '/#collections' },
  { label: '限時特惠', href: '/#hot-deals' },
  { label: '穿搭指南', href: '/style-guide' },
  { label: '關於我們', href: '/about' },
];

const policyLinks = [
  { label: '購物須知', content: '購物須知：所有訂單於確認付款後 1-3 個工作日出貨。滿 NT$1,500 免運費，未滿則收取 NT$100 運費。' },
  { label: '退換貨政策', content: '退換貨政策：收到商品後 7 天內可申請退換貨（商品須保持原狀、附原廠包裝與標籤）。特價品及內著類商品恕不退換。' },
  { label: '隱私權政策', content: '隱私權政策：我們重視您的個人資料安全，所有個資依法保護，不會對外販售或提供第三方使用。' },
  { label: '常見問題', content: '常見問題：如需協助，請透過 LINE 官方帳號 @outletTC 或電話 (04) 2320-8888 聯繫我們，服務時間同門市營業時間。' },
];

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [modal, setModal] = useState<{ title: string; content: string } | null>(null);

  const handleNavClick = (href: string) => {
    if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href.replace('/', ''));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      navigate(href);
    }
  };

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubStatus('loading');
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch('https://readdy.ai/api/form/d7f1vlaa509fjl5l02hg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubStatus('success');
        setEmail('');
      } else {
        setSubStatus('error');
      }
    } catch {
      setSubStatus('error');
    }
  };

  return (
    <>
      <footer className="bg-[#FAF5EB] border-t border-[#F0E6D0] pt-16 pb-8 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">

          {/* Top Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">

            {/* Brand */}
            <div className="lg:col-span-1">
              <img
                src="https://public.readdy.ai/ai/img_res/b54fcf7f-2746-4923-b8b9-39fce8de023d.png"
                alt="品牌 Logo"
                className="h-10 w-auto object-contain mb-5"
              />
              <p className="font-sans text-[1rem] text-[#2C2118]/60 leading-[1.75] mb-5">
                台中在地女性服飾 Outlet，以正品、透明與溫度，讓每位女性都能穿出自信品味。
              </p>
              {/* Social Icons */}
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/outlet.taichung"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-[#2C2118] text-white rounded-full hover:bg-[#C49A3E] transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-instagram-line text-base"></i>
                </a>
                <a
                  href="https://line.me/R/ti/p/@outletTC"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center bg-[#2C2118] text-white rounded-full hover:bg-[#06C755] transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-chat-smile-2-line text-base"></i>
                </a>
                <a
                  href="tel:0423208888"
                  className="w-9 h-9 flex items-center justify-center bg-[#2C2118] text-white rounded-full hover:bg-[#C49A3E] transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-phone-line text-base"></i>
                </a>
              </div>
            </div>

            {/* Shop Links */}
            <div>
              <h4 className="font-serif text-[1.0625rem] text-[#2C2118] font-semibold mb-5 tracking-wide">
                <a href="#shop">快速導覽</a>
              </h4>
              <ul className="space-y-3">
                {shopLinks.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={() => handleNavClick(l.href)}
                      className="font-sans text-[1rem] text-[#2C2118]/60 hover:text-[#C49A3E] transition-colors cursor-pointer"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Policy */}
            <div>
              <h4 className="font-serif text-[1.0625rem] text-[#2C2118] font-semibold mb-5 tracking-wide">
                <a href="#policy">服務條款</a>
              </h4>
              <ul className="space-y-3">
                {policyLinks.map((p) => (
                  <li key={p.label}>
                    <button
                      onClick={() => setModal({ title: p.label, content: p.content })}
                      className="font-sans text-[1rem] text-[#2C2118]/60 hover:text-[#C49A3E] transition-colors cursor-pointer text-left"
                    >
                      {p.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter + LINE CTA */}
            <div>
              <h4 className="font-serif text-[1.0625rem] text-[#2C2118] font-semibold mb-5 tracking-wide">
                <a href="#newsletter">訂閱電子報</a>
              </h4>
              <p className="font-sans text-[1rem] text-[#2C2118]/60 leading-[1.65] mb-4">
                第一手掌握新品上架與限時特惠資訊
              </p>
              <form
                data-readdy-form
                id="newsletter-subscribe"
                onSubmit={handleSubscribe}
                className="flex flex-col gap-3"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="輸入您的電子信箱"
                  required
                  className="w-full text-sm font-sans px-4 py-2.5 rounded-full border border-[#E8D7AE] bg-white text-[#2C2118] placeholder-[#2C2118]/35 outline-none focus:border-[#C49A3E] transition-colors"
                />
                <button
                  type="submit"
                  disabled={subStatus === 'loading'}
                  className="whitespace-nowrap w-full font-sans text-[1rem] font-medium bg-[#2C2118] text-white py-2.5 rounded-full hover:bg-[#C49A3E] transition-all duration-300 cursor-pointer disabled:opacity-60"
                >
                  {subStatus === 'loading' ? '訂閱中...' : subStatus === 'success' ? '訂閱成功！' : '立即訂閱'}
                </button>
                {subStatus === 'error' && (
                  <p className="font-sans text-sm text-red-500 text-center">訂閱失敗，請稍後再試</p>
                )}
              </form>

              {/* LINE CTA */}
              <a
                href="https://line.me/R/ti/p/@outletTC"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-4 flex items-center justify-center gap-2 w-full font-sans text-[1rem] font-medium border border-[#06C755] text-[#06C755] py-2.5 rounded-full hover:bg-[#06C755] hover:text-white transition-all duration-300 cursor-pointer"
              >
                <i className="ri-chat-smile-2-line text-lg"></i>
                加入 LINE 官方帳號
              </a>
            </div>

          </div>

          {/* Store Info Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6 border-t border-b border-[#EDE3CF] mb-8">
            <div className="flex items-center gap-2 font-sans text-[1rem] text-[#2C2118]/60">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-[#C49A3E]"></i>
              </span>
              台中市西區精誠路 168 號（近科博館）
            </div>
            <div className="flex items-center gap-2 font-sans text-[1rem] text-[#2C2118]/60">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-time-line text-[#C49A3E]"></i>
              </span>
              週一至五 11:00–21:00｜週六日 10:00–22:00
            </div>
            <div className="flex items-center gap-2 font-sans text-[1rem] text-[#2C2118]/60">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-[#C49A3E]"></i>
              </span>
              (04) 2320-8888
            </div>
          </div>

          {/* Copyright */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="font-sans text-[1rem] text-[#2C2118]/40">
              © 2024 Outlet Taichung. All rights reserved.
            </p>
            <p className="font-sans text-[1rem] text-[#2C2118]/40">
              100% 正品保障｜台中在地女裝 Outlet
            </p>
          </div>
        </div>
      </footer>

      {/* Policy Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/40 backdrop-blur-sm"
          onClick={() => setModal(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModal(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#2C2118]/40 hover:text-[#2C2118] cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <h3 className="font-serif text-[1.25rem] text-[#2C2118] font-semibold mb-4">{modal.title}</h3>
            <p className="font-sans text-[1rem] text-[#2C2118]/70 leading-[1.75]">{modal.content}</p>
          </div>
        </div>
      )}
    </>
  );
}
